import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import { companyID, getEmployeeID, getCompanyID } from "../../Common/UIComponents";
import { fetchFacilities } from "../../API_Service/Settings_Slice";
import { fetchFacilityAvailabilityByDate, createUpdateLabBooking } from "../../API_Service/LabBooking_Slice";
import BookingConfirmationModal from "../../Common/Modal/BookingConfirmationModal";


const MS_PER_DAY = 24 * 60 * 60 * 1000;

function dateKeyOf(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d, n) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}
function toDateInputValue(d) {
  return dateKeyOf(d);
}

function toLocalISOString(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
  );
}

const STATUS_COLOR = { booked: "#08757c", free: "#c3cccc" };

function isoWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((d - firstThursday) / (7 * 24 * 60 * 60 * 1000));
}


function buildGanttTasks(facilities, eventsByFacility) {
  const rows = [];
  let order = 0;

  const cityGroups = new Map(); // cityKey -> { cityName, facilities: [] }
  facilities.forEach((fac) => {
    const cityKey = fac.cityId != null && fac.cityId !== 0 ? String(fac.cityId) : "unassigned";
    if (!cityGroups.has(cityKey)) {
      cityGroups.set(cityKey, { cityName: fac.cityName || "Unassigned", facilities: [] });
    }
    cityGroups.get(cityKey).facilities.push(fac);
  });

  cityGroups.forEach((city, cityKey) => {
    const cityRowId = `city_${cityKey}`;
    rows.push({
      id: cityRowId,
      parentId: null,
      orderId: order++,
      title: city.cityName,
      start: new Date(),
      end: addDays(new Date(), 1),
      percentComplete: 0,
      summary: true,
      expanded: true,
      facilityId: null,
      events: [],
    });

    city.facilities.forEach((fac) => {
      const events = eventsByFacility.get(fac.id) ?? [];
      const starts = events.map((e) => e.start.getTime());
      const ends = events.map((e) => e.end.getTime());
      rows.push({
        id: `fac_${fac.id}`,
        parentId: cityRowId,
        orderId: order++,
        title: fac.name,
        start: starts.length ? new Date(Math.min(...starts)) : new Date(),
        end: ends.length ? new Date(Math.max(...ends)) : addDays(new Date(), 1),
        percentComplete: 0,
        summary: false,
        expanded: false,
        facilityId: fac.id,
        events,
      });
    });
  });

  return rows;
}

function taskCellTemplate(dataItem) {
  const encode = window.kendo ? window.kendo.htmlEncode : (s) => s;
  const color = dataItem.summary ? "#181c1d" : STATUS_COLOR.booked;
  const weight = dataItem.summary ? 700 : 500;
  return `<span style="color:${color};font-weight:${weight};">${encode(dataItem.title || "")}</span>`;
}

export default function FacilityBookingScheduler() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ganttElRef = useRef(null);
  const widgetRef = useRef(null);

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  
  const [toast, setToast] = useState(null);
  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const today = new Date();
  const [fromDate, setFromDate] = useState(toDateInputValue(today));
  
  const [toDate, setToDate] = useState(
    toDateInputValue(new Date(today.getFullYear(), 9, 31)),
  );
  const [dateError, setDateError] = useState("");
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
 
  const [bookingRefreshKey, setBookingRefreshKey] = useState(0);

 
  const pendingStartRef = useRef(null);
  const dragAnchorRef = useRef(null);
  const isDraggingRef = useRef(false);
  const pressDownRef = useRef(false);
  const highlightElRef = useRef(null);
  const bookingOverlayContainerRef = useRef(null);
  const [pendingStartInfo, setPendingStartInfo] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingSummary, setBookingSummary] = useState(null);
  
  const bookingSubmittingRef = useRef(false);
 
  const selectedViewTypeRef = useRef("month"); // "Week" tab, today's default

  const cancelPendingStart = () => {
    pendingStartRef.current = null;
    dragAnchorRef.current = null;
    isDraggingRef.current = false;
    pressDownRef.current = false;
    if (highlightElRef.current) {
      highlightElRef.current.remove();
      highlightElRef.current = null;
    }
    setPendingStartInfo(null);
  };

  // Esc cancels a booking drag in progress.
  useEffect(() => {
    if (!pendingStartInfo) return;
    const handler = (e) => {
      if (e.key === "Escape") cancelPendingStart();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pendingStartInfo]);

  const handleFromDateChange = (val) => {
    setFromDate(val);
    setDateError(toDate && val > toDate ? "Start date cannot be later than end date." : "");
  };
  const handleToDateChange = (val) => {
    if (fromDate && val < fromDate) {
      setDateError("End date cannot be earlier than start date.");
      setToDate(val);
      return;
    }
    setToDate(val);
    setDateError("");
  };

  // ── 1. Load the facility list once ──────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    dispatch(fetchFacilities({ ID: 0, companyID }))
      .then((res) => {
        const rows = res?.payload?.data;
        if (!res?.payload?.success || !Array.isArray(rows)) {
          setLoadError(true);
          return;
        }
        const mapped = rows
          .filter((r) => (r.Status ?? "AC") === "AC")
          .map((r) => ({
            id: Number(r.Facility_Id),
            name: r.Facility_Name ?? `Facility ${r.Facility_Id}`,
           
            cityId: r.CityID ?? null,
            cityName: r.City_Name ?? null,
          }));
        if (mapped.length === 0) {
          setLoadError(true);
          return;
        }
        setFacilities(mapped);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (facilities.length === 0) return;
    if (fromDate && toDate && fromDate > toDate) return;

    const start = new Date(`${fromDate}T00:00:00`);
    const end = new Date(`${toDate}T00:00:00`);
    const days = Math.max(1, Math.round((end - start) / MS_PER_DAY) + 1);
    const dateKeys = Array.from({ length: days }, (_, i) => dateKeyOf(addDays(start, i)));

    setTasksLoading(true);
    Promise.all(
      dateKeys.map((date) => dispatch(fetchFacilityAvailabilityByDate({ companyID, date }))),
    )
      .then((results) => {
        const seenBookingIds = new Set();
        const eventsByFacility = new Map();
        results.forEach((res) => {
          const rows = res?.payload?.data;
          if (!res?.payload?.success || !Array.isArray(rows)) return;
          rows.forEach((r) => {
            if (r.bookingId == null || seenBookingIds.has(r.bookingId)) return;
            seenBookingIds.add(r.bookingId);
            const facId = Number(r.facilityId);
            const list = eventsByFacility.get(facId) ?? [];
            list.push({
              bookingId: r.bookingId,
              title: r.bookingTestId ? `Booked · Test #${r.bookingTestId}` : "Booked",
              start: new Date(r.plannedStartDateHrs),
              end: new Date(r.plannedEndDateHrs),
            });
            eventsByFacility.set(facId, list);
          });
        });
        setTasks(buildGanttTasks(facilities, eventsByFacility));
      })
      .catch(() => setTasks(buildGanttTasks(facilities, new Map())))
      .finally(() => setTasksLoading(false));
  }, [facilities, fromDate, toDate, dispatch, bookingRefreshKey]);

  const filteredTasks = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tasks;
    
    const cityTitleById = new Map(tasks.filter((t) => t.summary).map((t) => [t.id, t.title.toLowerCase()]));
    const matchingFacilityIds = new Set(
      tasks
        .filter((t) => !t.summary)
        .filter((t) => t.title.toLowerCase().includes(q) || (cityTitleById.get(t.parentId) || "").includes(q))
        .map((t) => t.id),
    );
    const neededCityIds = new Set(
      tasks.filter((t) => !t.summary && matchingFacilityIds.has(t.id)).map((t) => t.parentId),
    );
    return tasks.filter((t) => (t.summary && neededCityIds.has(t.id)) || matchingFacilityIds.has(t.id));
  })();

  useEffect(() => {
    const $ = window.$ || window.jQuery;
    const kendo = window.kendo;
    if (!$ || !kendo || !ganttElRef.current) return;
    if (loading || loadError || tasksLoading) return;

   
    pendingStartRef.current = null;
    dragAnchorRef.current = null;
    isDraggingRef.current = false;
    pressDownRef.current = false;
    highlightElRef.current = null; // the old node is gone with the destroyed widget's DOM
    bookingOverlayContainerRef.current = null; // ditto
    setPendingStartInfo(null);

    const $el = $(ganttElRef.current);
    const existingWidget = $el.data("kendoGantt");
    if (existingWidget) existingWidget.destroy();
    if (widgetRef.current && widgetRef.current !== existingWidget) widgetRef.current.destroy();
    $el.empty();
    widgetRef.current = null;

    
    const rawStart = new Date(`${fromDate}T00:00:00`);
    const rawEnd = new Date(`${toDate}T00:00:00`);
    const rangeStart = new Date(rawStart.getFullYear(), rawStart.getMonth(), 1);
    const rangeEnd = new Date(rawEnd.getFullYear(), rawEnd.getMonth() + 1, 0);
    const totalDays = Math.max(1, Math.round((rangeEnd - rangeStart) / MS_PER_DAY));
    const timelineWidth = Math.max(0, ($el.width() || 0) - 260 - 20); // minus the Facility column + scrollbar/border slack
    const fitSlotSize = (slotCount, min) => Math.max(min, Math.floor(timelineWidth / Math.max(slotCount, 1)));
    
    const daySlotSize = fitSlotSize(totalDays, 100);
    const weekSlotSize = fitSlotSize(Math.ceil(totalDays / 7), 90);
    const monthSlotSize = fitSlotSize(Math.ceil(totalDays / 30), 120);

    // Scopes the CSS below to just this Gantt instance.
    $el.addClass("facility-booking-gantt");

    const TASK_BAR_STYLE_ID = "facility-booking-taskbar-style";
    {
     
      let styleEl = document.getElementById(TASK_BAR_STYLE_ID);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = TASK_BAR_STYLE_ID;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = `
        .facility-booking-gantt .k-task,
        .facility-booking-gantt .k-task-single,
        .facility-booking-gantt .k-task-wrap { background: transparent !important; border-color: transparent !important; box-shadow: none !important; }
        .facility-booked-slot {
          position: absolute;
          border-radius: 6px;
          background: linear-gradient(135deg, #0f8b91, #08757c);
          box-shadow: 0 2px 6px rgba(8,117,124,0.35);
          pointer-events: none;
          z-index: 4;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          padding: 0 8px;
          box-sizing: border-box;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
        }
        .facility-booking-gantt .k-view-selector .k-selected,
        .facility-booking-gantt .k-selected {
          background: #08757c !important;
          border-color: #08757c !important;
          color: #fff !important;
        }
        .facility-booking-gantt .k-selected:hover {
          background: #0a8f97 !important;
          border-color: #0a8f97 !important;
        }
        /* Toolbar (Day/Week/Month) + the column-header bands (month/week
           strip, "Facility" column header) — a soft teal tint instead of
           the flat default white/grey, tying the header chrome into the
           app's accent color. */
        .facility-booking-gantt .k-toolbar,
        .facility-booking-gantt .k-grid-header,
        .facility-booking-gantt .k-grid-header-wrap {
          background: #eaf4f4 !important;
          border-color: #9cc9c9 !important;
        }
        .facility-booking-gantt .k-grid-header .k-header,
        .facility-booking-gantt .k-grid-header th {
          color: #0a5a60;
          font-weight: 700;
          border-color: #9cc9c9 !important;
        }
      `;
    }

    let renderBookingOverlaysHandle = null;

    try {
      $el.kendoGantt({
        
        range: {
          start: rangeStart,
          end: rangeEnd,
        },
        dataSource: {
          data: filteredTasks,
          schema: {
            model: {
              id: "id",
              fields: {
                id: { type: "string" },
                parentId: { type: "string", defaultValue: null },
                orderId: { type: "number" },
                title: { type: "string" },
                start: { type: "date" },
                end: { type: "date" },
                percentComplete: { type: "number" },
                summary: { type: "boolean" },
                expanded: { type: "boolean" },
                facilityId: { type: "number" },
              },
            },
          },
        },
        columns: [
          { field: "title", title: "Location / Facility", width: 260, editable: false, template: taskCellTemplate },
        ],
       
        views: [
          {
            type: "week",
            title: "Day",
            slotSize: daySlotSize,
           
            selected: selectedViewTypeRef.current === "week",
            
            range: { start: rawStart, end: rangeEnd },
          },
          {
            type: "month",
            title: "Week",
            selected: selectedViewTypeRef.current === "month",
            slotSize: weekSlotSize,
            
            weekHeaderTemplate: (data) => `CW${String(isoWeekNumber(data.start)).padStart(2, "0")}`,
          
            monthHeaderTemplate: (data) =>
              `${data.start.toLocaleDateString(undefined, { month: "short" })}/${String(data.start.getFullYear()).slice(-2)}`,
          },
          {
            type: "year",
            title: "Month",
            selected: selectedViewTypeRef.current === "year",
            slotSize: monthSlotSize,
          },
        ],
        editable: false,
       
        selectable: false,
        filterable: true,
        rowHeight: 40,
        height: 700,
        showWorkHours: false,
        showWorkDays: false,
       
        tooltip: { visible: false },
      });
      widgetRef.current = $el.data("kendoGantt");

     
      const resolveTimeAtPoint = (clientX) => {
        const view = widgetRef.current?.timeline?.view?.();
        const $content = view?.content;
        if (!$content || !$content.length) return null;
        const slots = typeof view._timeSlots === "function" ? view._timeSlots() : [];
        if (!slots.length) return null;
        const rect = $content[0].getBoundingClientRect();
        const scrollLeft = $content[0].scrollLeft || 0;
        const relativeX = clientX - rect.left + scrollLeft;
        for (let i = 0; i < slots.length; i++) {
          const slot = slots[i];
          if (slot.offsetLeft == null || slot.offsetWidth == null) continue;
          if (relativeX >= slot.offsetLeft && relativeX < slot.offsetLeft + slot.offsetWidth) {
            const ratio = slot.offsetWidth > 0 ? (relativeX - slot.offsetLeft) / slot.offsetWidth : 0;
            const t = new Date(slot.start.getTime() + ratio * (slot.end.getTime() - slot.start.getTime()));
            t.setMinutes(0, 0, 0);
            return t;
          }
        }
        // Dragged past an edge — clamp to whichever boundary is closer.
        const first = slots[0];
        const last = slots[slots.length - 1];
        const clamped = relativeX < first.offsetLeft ? new Date(first.start) : new Date(last.end);
        clamped.setMinutes(0, 0, 0);
        return clamped;
      };
      
      const pixelAtTime = (date) => {
        const view = widgetRef.current?.timeline?.view?.();
        const slots = typeof view?._timeSlots === "function" ? view._timeSlots() : [];
        if (!slots.length) return null;
        const t = date.getTime();
        for (let i = 0; i < slots.length; i++) {
          const slot = slots[i];
          if (slot.offsetLeft == null || slot.offsetWidth == null) continue;
          if (t >= slot.start.getTime() && t < slot.end.getTime()) {
            const ratio = (t - slot.start.getTime()) / (slot.end.getTime() - slot.start.getTime());
            return slot.offsetLeft + ratio * slot.offsetWidth;
          }
        }
        const first = slots[0];
        const last = slots[slots.length - 1];
        if (t < first.start.getTime()) return first.offsetLeft;
        return last.offsetLeft + last.offsetWidth;
      };

      
      
      const TOOLTIP_ID = "facility-booking-overlay-tooltip";
      let tooltipEl = document.getElementById(TOOLTIP_ID);
      if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = TOOLTIP_ID;
        Object.assign(tooltipEl.style, {
          position: "fixed",
          pointerEvents: "none",
          zIndex: "100000",
          display: "none",
          background: "#181c1d",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          lineHeight: "1.5",
          maxWidth: "260px",
          boxShadow: "0 10px 28px rgba(0,0,0,0.4)",
        });
        document.body.appendChild(tooltipEl);
      }
      const rangeLabel = (d) =>
        d.toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });

      
      const eventsByTaskId = {};
      filteredTasks.forEach((t) => {
        eventsByTaskId[t.id] = t.events || [];
      });

      const renderBookingOverlays = () => {
        const view = widgetRef.current?.timeline?.view?.();
        const $content = view?.content;
        if (!$content || !$content.length) return;
        if (window.getComputedStyle($content[0]).position === "static") {
          $content[0].style.position = "relative";
        }
        let container = bookingOverlayContainerRef.current;
        if (!container) {
          container = document.createElement("div");
          container.style.position = "absolute";
          container.style.top = "0";
          container.style.left = "0";
          container.style.width = "0";
          container.style.height = "0";
          $content[0].appendChild(container);
          bookingOverlayContainerRef.current = container;
        }
        container.innerHTML = "";
        const rowHeight = view._rowHeight || 40;
        
        widgetRef.current.dataSource.view().forEach((task, rowIndex) => {
          if (task.summary) return; // city rows never carry bookings themselves
          (eventsByTaskId[task.id] || []).forEach((ev) => {
            const left = pixelAtTime(ev.start);
            const right = pixelAtTime(ev.end);
            if (left == null || right == null) return;
            const box = document.createElement("div");
            box.className = "facility-booked-slot";
            box.style.top = `${rowIndex * rowHeight + 4}px`;
            box.style.left = `${Math.min(left, right)}px`;
            box.style.width = `${Math.max(Math.abs(right - left), 3)}px`;
            box.style.height = `${Math.max(rowHeight - 8, 8)}px`;
            box.style.lineHeight = box.style.height;
            box.style.pointerEvents = "auto";
            box.style.cursor = "default";
            box.textContent = task.title;
            box.addEventListener("mouseenter", () => {
              tooltipEl.innerHTML =
                `<div style="font-weight:700;margin-bottom:3px;">${kendo.htmlEncode(task.title)}</div>` +
                `<div style="font-weight:500;color:#c3cccc;">${rangeLabel(ev.start)} → ${rangeLabel(ev.end)}</div>`;
              tooltipEl.style.display = "block";
            });
            box.addEventListener("mousemove", (moveEvent) => {
              tooltipEl.style.left = `${moveEvent.clientX + 14}px`;
              tooltipEl.style.top = `${moveEvent.clientY + 14}px`;
            });
            box.addEventListener("mouseleave", () => {
              tooltipEl.style.display = "none";
            });
            container.appendChild(box);
          });
        });
      };
      renderBookingOverlaysHandle = renderBookingOverlays;
      renderBookingOverlays();
      widgetRef.current.bind("navigate", (e) => {
        if (e.view) selectedViewTypeRef.current = e.view;
        setTimeout(renderBookingOverlays, 0);
      });
      window.addEventListener("resize", renderBookingOverlays);
      
      $el.find(".k-gantt-treelist").on("click.facilityBookingOverlay", () => {
        setTimeout(() => {
          
          widgetRef.current?.resize?.(true);
          renderBookingOverlays();
        }, 0);
      });

      const hourLabel = (d) =>
        d.toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });

      const HIGHLIGHT_STYLE_ID = "facility-booking-drag-style";
      {
        // Same re-sync-on-every-mount fix as the taskbar style tag above.
        let styleEl = document.getElementById(HIGHLIGHT_STYLE_ID);
        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.id = HIGHLIGHT_STYLE_ID;
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = `
          .facility-booking-drag-box {
            position: absolute;
            border-radius: 8px;
            border: 2px solid #08757c;
            background: linear-gradient(135deg, rgba(8,117,124,0.32), rgba(45,212,191,0.16));
            box-shadow: 0 6px 16px rgba(8,117,124,0.30), inset 0 1px 0 rgba(255,255,255,0.55);
            pointer-events: none;
            z-index: 5;
            animation: facilityBookingDragPulse 1.6s ease-in-out infinite;
          }
          .facility-booking-drag-box::before,
          .facility-booking-drag-box::after {
            content: "";
            position: absolute;
            top: 50%;
            width: 12px;
            height: 12px;
            background: #08757c;
            border: 2px solid #fff;
            border-radius: 50%;
            transform: translate(-50%, -50%);
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
          }
          .facility-booking-drag-box::before { left: 0; }
          .facility-booking-drag-box::after { left: 100%; }
          @keyframes facilityBookingDragPulse {
            0%, 100% { box-shadow: 0 6px 16px rgba(8,117,124,0.30), inset 0 1px 0 rgba(255,255,255,0.55); }
            50% { box-shadow: 0 6px 22px rgba(8,117,124,0.5), inset 0 1px 0 rgba(255,255,255,0.55); }
          }
          @media (prefers-reduced-motion: reduce) {
            .facility-booking-drag-box { animation: none; }
          }
        `;
      }

      const drawHighlight = (rowIndex, leftPx, rightPx) => {
        const view = widgetRef.current?.timeline?.view?.();
        const $content = view?.content;
        if (!$content || !$content.length || rowIndex == null || rowIndex < 0) return;
        if (window.getComputedStyle($content[0]).position === "static") {
          $content[0].style.position = "relative";
        }
        let el = highlightElRef.current;
        if (!el) {
          el = document.createElement("div");
          el.className = "facility-booking-drag-box";
          $content[0].appendChild(el);
          highlightElRef.current = el;
        }
        const rowHeight = view._rowHeight || 40;
        const left = Math.min(leftPx, rightPx);
        const width = Math.max(Math.abs(rightPx - leftPx), 2);
        el.style.top = `${rowIndex * rowHeight + 3}px`;
        el.style.left = `${left}px`;
        el.style.width = `${width}px`;
        el.style.height = `${Math.max(rowHeight - 6, 10)}px`;
      };
      const removeHighlight = () => {
        if (highlightElRef.current) {
          highlightElRef.current.remove();
          highlightElRef.current = null;
        }
      };

      $el.on("mousedown.facilityBooking", (e) => {
        const widget = widgetRef.current;
        if (!widget) return;

        
        if (!$(e.target).closest(".k-gantt-timeline").length) return;

        let item = null;
        const $uidRow = $(e.target).closest("[data-uid]");
        if ($uidRow.length) {
          item = widget.dataSource.getByUid($uidRow.attr("data-uid"));
        }
        if (!item) {
          const view = widget.timeline?.view?.();
          const $content = view?.content;
          if ($content && $content.length) {
            const rect = $content[0].getBoundingClientRect();
            const scrollTop = $content[0].scrollTop || 0;
            const relativeY = e.clientY - rect.top + scrollTop;
            const rowHeight = view._rowHeight || 40;
            const rowIndex = Math.floor(relativeY / rowHeight);
            item = widget.dataSource.view()[rowIndex];
          }
        }
        if (!item) return;
        // A City row is just a grouping header — nothing to book there.
        if (item.summary) return;

        const rowIndex = widget.dataSource.view().indexOf(item);

        const start = resolveTimeAtPoint(e.clientX);
        if (!start) return;

        // Don't let a booking start in the past.
        if (start.getTime() < Date.now()) {
          showToast("Can't book a time that has already passed", true);
          return;
        }

        
        const startMs = start.getTime();
        const alreadyBooked = (eventsByTaskId[item.id] || []).some(
          (ev) => startMs >= ev.start.getTime() && startMs < ev.end.getTime(),
        );
        if (alreadyBooked) {
          showToast(`${item.title} is already booked at that time`, true);
          return;
        }

        e.preventDefault(); // no text selection / Kendo drag hints while dragging
        pressDownRef.current = true;
        dragAnchorRef.current = { x: e.clientX, y: e.clientY };
        isDraggingRef.current = false;

        if (!pendingStartRef.current || pendingStartRef.current.item.id !== item.id) {
          pendingStartRef.current = { item, start, rowIndex };
          setPendingStartInfo({ lab: item.title, startLabel: hourLabel(start), endLabel: hourLabel(start) });
        }

        const pendingNow = pendingStartRef.current;
        const anchorPx = pixelAtTime(pendingNow.start);
        if (anchorPx != null) drawHighlight(pendingNow.rowIndex, anchorPx, anchorPx);
      });

      $(document).on("mousemove.facilityBookingDrag", (e) => {
        if (!pressDownRef.current || !pendingStartRef.current) return;
        const anchor = dragAnchorRef.current;
        if (!isDraggingRef.current && anchor) {
          const dx = e.clientX - anchor.x;
          const dy = e.clientY - anchor.y;
          
          if (Math.hypot(dx, dy) > 6) isDraggingRef.current = true;
        }

        const time = resolveTimeAtPoint(e.clientX);
        if (!time) return;
        setPendingStartInfo((prev) => (prev ? { ...prev, endLabel: hourLabel(time) } : prev));

        const pending = pendingStartRef.current;
        const startPx = pixelAtTime(pending.start);
        const currentPx = pixelAtTime(time);
        if (startPx != null && currentPx != null) {
          drawHighlight(pending.rowIndex, startPx, currentPx);
        }
      });

      $(document).on("mouseup.facilityBookingDrag", (e) => {
        if (!pressDownRef.current) return;
        pressDownRef.current = false;
        const pending = pendingStartRef.current;
        if (!pending) return;

        
        if (!isDraggingRef.current) return;

        removeHighlight();
        const start = pending.start;
        let end = resolveTimeAtPoint(e.clientX) || new Date(start.getTime() + 60 * 60 * 1000);
        if (end.getTime() <= start.getTime()) {
          end = new Date(start.getTime() + 60 * 60 * 1000);
        }
        setBookingSummary({
          lab: pending.item.title,
          facilityId: pending.item.facilityId,
          start: toLocalISOString(start),
          end: toLocalISOString(end),
        });
        setBookingModalOpen(true);
        pendingStartRef.current = null;
        isDraggingRef.current = false;
        setPendingStartInfo(null);
      });
    } catch (err) {
      console.error("kendoGantt init failed:", err);
      setLoadError(true);
    }

    return () => {
      $el.off("mousedown.facilityBooking");
      $el.find(".k-gantt-treelist").off("click.facilityBookingOverlay");
      $(document).off("mousemove.facilityBookingDrag mouseup.facilityBookingDrag");
      if (renderBookingOverlaysHandle) window.removeEventListener("resize", renderBookingOverlaysHandle);
      
      const staleTooltip = document.getElementById("facility-booking-overlay-tooltip");
      if (staleTooltip) staleTooltip.style.display = "none";
      if (widgetRef.current) {
        widgetRef.current.destroy();
        widgetRef.current = null;
      }
    };
    
  }, [filteredTasks, loading, loadError, tasksLoading]);

  const handleBookingConfirm = (modalData) => {
    
    console.log("[FacilityBooking] handleBookingConfirm called", { modalData, bookingSummary });
    try {
      if (!bookingSummary?.facilityId) {
        console.warn("[FacilityBooking] no facilityId on bookingSummary", bookingSummary);
        showToast("Couldn't determine which facility this booking is for", true);
        return;
      }
      if (bookingSubmittingRef.current) {
        console.log("[FacilityBooking] ignored — a create is already in flight");
        return;
      }
      bookingSubmittingRef.current = true;

      const payload = {
        BookingId: 0,
        FacilityId: bookingSummary.facilityId,
        Planned_Start_Date_Hrs: modalData.startDateTimeStr,
        Planned_End_Date_Hrs: modalData.endDateTimeStr,
        Planned_Hrs: modalData.durationHours,
        Project_ID: modalData.projectId || "",
        Project_Test_Det_ID: modalData.testTypeId || "",
        Booking_Remarks: modalData.remarks || "",
        Actual_Hrs: 0,
        TO_EmployeeID: getEmployeeID(),
        Created_By: getEmployeeID(),
        Modified_By: getEmployeeID(),
        CompanyID: getCompanyID(),
      };
      console.log("[FacilityBooking] dispatching createUpdateLabBooking", payload);

      dispatch(createUpdateLabBooking(payload))
        .then((res) => {
          console.log("[FacilityBooking] createUpdateLabBooking response", res);
          const result = res?.payload;
          if (result?.success) {
            showToast("Booking confirmed successfully. It will be visible here once approved.");
            setBookingModalOpen(false);
            setBookingRefreshKey((k) => k + 1); // pull the new booking into the chart
          } else {
            showToast(result?.message || "Couldn't create the booking", true);
          }
        })
        .catch((err) => {
          console.error("[FacilityBooking] createUpdateLabBooking rejected", err);
          showToast("Couldn't create the booking", true);
        })
        .finally(() => {
          bookingSubmittingRef.current = false;
        });
    } catch (err) {
      console.error("[FacilityBooking] handleBookingConfirm threw", err);
      showToast("Something went wrong confirming the booking", true);
      bookingSubmittingRef.current = false;
    }
  };

  return (
    <DashboardLayout>
      <div className="px-8 py-6 bg-[#f4f8f8] min-h-screen text-[#181c1d]">
        {/* ── Breadcrumb ── */}
       

        <div className="bg-white rounded-[28px] shadow-[0_24px_48px_-16px_rgba(8,117,124,0.16)] border border-[#e5e9e9]/70 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-7 py-5 border-b border-[#eef2f2] flex-shrink-0 flex-wrap gap-4">
            <div className="min-w-0 flex items-center gap-4">
              <span className="hidden sm:flex w-11 h-11 rounded-2xl bg-gradient-to-br from-[#08757c] to-[#0a9aa3] text-white items-center justify-center flex-shrink-0 shadow-[0_8px_18px_rgba(8,117,124,0.35)]">
                <span className="material-symbols-outlined text-[22px]">event_available</span>
              </span>
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="group flex items-center gap-1 text-[11px] font-semibold text-[#6e797a] hover:text-[#08757c] transition-colors mb-1"
                >
                  <span className="material-symbols-outlined text-[15px] transition-transform group-hover:-translate-x-0.5">
                    arrow_back
                  </span>
                  Back
                </button>
                <h1 className="text-xl font-extrabold tracking-tight text-[#181c1d]">Facility Booking</h1>
                <p className="text-xs text-[#9eabac] mt-0.5">
                  Labs down the side, Day / Week / Month across the top.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 bg-[#f4f8f8] border border-[#e5e9e9] rounded-2xl px-1 py-1">
                <label className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-[11px] font-semibold text-[#6e797a]">
                  <span className="material-symbols-outlined text-[15px] text-[#9eabac]">calendar_month</span>
                  From
                  <input
                    type="date"
                    value={fromDate}
                    max={toDate || undefined}
                    onChange={(e) => handleFromDateChange(e.target.value)}
                    className="bg-transparent text-xs font-semibold text-[#181c1d] focus:outline-none"
                  />
                </label>
                <span className="w-px h-4 bg-[#e5e9e9]" />
                <label className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 text-[11px] font-semibold text-[#6e797a]">
                  To
                  <input
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={(e) => handleToDateChange(e.target.value)}
                    className="bg-transparent text-xs font-semibold text-[#181c1d] focus:outline-none"
                  />
                </label>
              </div>
              {dateError && (
                <span className="text-[11px] font-semibold text-[#ba1a1a]">{dateError}</span>
              )}
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-[#9eabac]">
                  search
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Filter location, facilities"
                  className="pl-9 pr-3 py-2 text-xs font-medium rounded-2xl border border-[#e5e9e9] bg-[#f4f8f8] focus:outline-none focus:ring-2 focus:ring-[#08757c]/25 focus:bg-white transition-colors w-48"
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {loading ? (
              <div className="flex items-center justify-center gap-3 text-[#6e797a] text-sm py-16">
                <span className="material-symbols-outlined animate-spin text-[#08757c]">progress_activity</span>
                Loading facilities…
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center justify-center gap-2 text-center py-16">
                <span className="material-symbols-outlined text-[28px] text-[#ba1a1a]">error</span>
                <p className="text-sm font-semibold text-[#181c1d]">Couldn&apos;t load the scheduler</p>
                <p className="text-xs text-[#9eabac] max-w-xs">
                  Either no active facilities came back from Settings_GetFacilityList, or Kendo UI
                  hasn&apos;t finished loading on the page yet.
                </p>
              </div>
            ) : (
             
              <div className="relative">
                <div ref={ganttElRef} />
                {tasksLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-2xl bg-white/70 backdrop-blur-[1px] text-sm font-semibold text-[#08757c]">
                    <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
                    Loading bookings…
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {pendingStartInfo && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[99996] flex items-center gap-3 bg-[#181c1d] text-white text-sm font-semibold rounded-full pl-5 pr-2 py-2 shadow-[0_12px_32px_rgba(0,0,0,0.35)]">
          <span className="material-symbols-outlined text-[18px] text-emerald-400">
            schedule
          </span>
          <span>
            {pendingStartInfo.lab} · {pendingStartInfo.startLabel}
            <span className="text-white/60 font-medium"> → </span>
            {pendingStartInfo.endLabel}
            <span className="text-white/60 font-medium"> — release to book</span>
          </span>
          <button
            type="button"
            onClick={cancelPendingStart}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 flex-shrink-0"
            title="Cancel (Esc)"
          >
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
      )}

      <BookingConfirmationModal
        isOpen={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        onConfirm={handleBookingConfirm}
        bookingSummary={bookingSummary}
      />

      {/* ── Toast — same look as LabBookingRequest.js's bottom-center banner ── */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold
          ${toast.isError ? "bg-[#ba1a1a] text-white" : "bg-[#bbf46e] text-[#112000]"}`}
        >
          <span className="material-symbols-outlined text-[18px]">
            {toast.isError ? "error" : "check_circle"}
          </span>
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
